import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from "@mui/material/styles";
import {
    Container, Card, Typography, CircularProgress, Alert, Box, Grid, Button, Dialog,
    Breadcrumbs, Link
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HomeIcon from '@mui/icons-material/Home';
import BuildingDetailsFloors from "../components/BuildingDetails/BuildingDetailsFloors";
import BuildingDetailsWalls from "../components/BuildingDetails/BuildingDetailsWalls";
import EditBuilding from "./EditBuilding";
import Unauthorized from "../Layout/Unauthorized";
import BuildingDetailsInsights from "../components/BuildingDetails/BuildingDetailsInsights";
import BuildingDetailsAppliances from "../components/BuildingDetails/BuildingDetailsAppliances";
import BuildingDetailsHotWater from "../components/BuildingDetails/BuildingDetailsHotWater";
import BuildingDetailsHeatingSystems from "../components/BuildingDetails/BuildingDetailsHeatingSystems";
import BuildingDetailsLamps from "../components/BuildingDetails/BuildingDetailsLamps";
import BuildingDetailsACs from "../components/BuildingDetails/BuildingDetailsACs";
import BuildingDetailsCeilings from "../components/BuildingDetails/BuildingDetailsCeilings";
import { useUserContext } from '../contexts/UserContext';

const BuildingDetails = () => {
    const theme = useTheme();
    const { token } = useUserContext();
    const { id } = useParams();
    const navigate = useNavigate();

    const [building, setBuilding] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [view, setView] = useState('floors');
    const [editOpen, setEditOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const fetchBuilding = useCallback(() => {
        axios.get(`/buildings/${id}/`)
            .then(response => {
                setBuilding(response.data);
                setLoading(false);
            })
            .catch(() => {
                setError(true);
                setLoading(false);
            });
    }, [id]);

    useEffect(() => {
        if (token) fetchBuilding();
    }, [token, id, fetchBuilding]);

    const handleDelete = () => {
        setDeleting(true);
        axios.delete(`/buildings/${id}/`)
            .then(() => {
                setDeleting(false);
                setDeleteDialogOpen(false);
                navigate('/buildings');
            })
            .catch(() => {
                setDeleting(false);
                setDeleteDialogOpen(false);
            });
    };

    if (!token) return <Unauthorized />;
    if (loading) {
        return (
            <Container sx={{ mt: 5, textAlign: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error || !building) {
        return (
            <Container sx={{ mt: 5 }}>
                <Alert severity="error">Failed to load building details.</Alert>
            </Container>
        );
    }

    const lat = Number(building.lat);
    const lon = Number(building.lon);
    const hasCoordinates = !isNaN(lat) && !isNaN(lon);

    return (
        <Container maxWidth="xl" sx={{ mt: 5 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                <Link
                    underline="hover"
                    color="inherit"
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/');
                    }}
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Home
                </Link>
                <Link
                    underline="hover"
                    color="inherit"
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/buildings');
                    }}
                >
                    Buildings
                </Link>
                <Typography color="text.primary">{building.name}</Typography>
            </Breadcrumbs>

            <Card elevation={3} sx={{ borderRadius: 3 }}>
                <Box sx={{
                    bgcolor: theme.palette.secondary.main,
                    px: 3,
                    py: 2,
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff' }}>
                        Building Details
                    </Typography>
                    <Box>
                        <Button
                            variant="outlined"
                            sx={{ color: '#fff', borderColor: '#fff', mr: 2 }}
                            onClick={() => setEditOpen(true)}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            sx={{ borderColor: '#fff', color: '#fff' }}
                            onClick={() => setDeleteDialogOpen(true)}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>

                <Grid container spacing={0} sx={{ backgroundColor: '#f5f9f6', p: 4 }}>
                    <Grid item xs={12} md={6}>
                        <Grid container spacing={3}>
                            <Grid item xs={6}><Typography variant="subtitle2">Name</Typography><Typography
                                variant="body1" sx={{ fontWeight: 'bold' }}>{building.name}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="subtitle2">Electricity Price (€ /
                                kWh)</Typography><Typography variant="body1"
                                    sx={{ fontWeight: 'bold' }}>{building.electricity_price}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="subtitle2">Gas Price (€ / m³)</Typography><Typography
                                variant="body1" sx={{ fontWeight: 'bold' }}>{building.gas_price}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="subtitle2">Oil Price (€ / lt)</Typography><Typography
                                variant="body1" sx={{ fontWeight: 'bold' }}>{building.oil_price}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="subtitle2">Indoor Temperature
                                (°C)</Typography><Typography variant="body1"
                                    sx={{ fontWeight: 'bold' }}>{building.T_inside_heating}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="subtitle2">Daily Hours</Typography><Typography
                                variant="body1" sx={{ fontWeight: 'bold' }}>{building.daily_hours}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="subtitle2">Nightly Hours</Typography><Typography
                                variant="body1" sx={{ fontWeight: 'bold' }}>{building.nightly_hours}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="subtitle2">Latitude</Typography><Typography
                                variant="body1"
                                sx={{ fontWeight: 'bold' }}>{hasCoordinates ? lat : '-'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="subtitle2">Longitude</Typography><Typography
                                variant="body1"
                                sx={{ fontWeight: 'bold' }}>{hasCoordinates ? lon : '-'}</Typography></Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ height: 300, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
                            {hasCoordinates ? (
                                <iframe
                                    title="Building Location"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    loading="lazy"
                                    allowFullScreen
                                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${lon - 0.005}%2C${lat - 0.005}%2C${lon + 0.005}%2C${lat + 0.005}&layer=mapnik&marker=${lat}%2C${lon}`}
                                />

                            ) : (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%'
                                }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Location data not available.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    {/* Optimization Resources Section */}
                    <Grid item xs={12}>
                        <Box sx={{
                            mt: 4,
                            p: 3,
                            borderRadius: 2,
                            bgcolor: 'white',
                            border: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
                                Optimization Resources
                            </Typography>
                            <Grid container spacing={3} alignItems="stretch">
                                <Grid item xs={12} md={9}>
                                    <Grid container spacing={3} sx={{ height: '100%' }}>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{
                                                p: 3,
                                                borderRadius: 2,
                                                bgcolor: 'primary.light',
                                                color: 'primary.contrastText',
                                                textAlign: 'center',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                minHeight: 120
                                            }}>
                                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                    {building.budget ? `${building.budget.toLocaleString()} €` : '0 €'}
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>Available Budget</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{
                                                p: 3,
                                                borderRadius: 2,
                                                bgcolor: 'primary.light',
                                                color: 'success.contrastText',
                                                textAlign: 'center',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                minHeight: 120
                                            }}>
                                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                    {building.available_area ? `${building.available_area.toLocaleString()} m²` : '0 m²'}
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>Available Area for PV</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        height: '100%'
                                    }}>
                                        <Box
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                p: 2,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'primary.main',
                                                color: 'primary.main',
                                                textAlign: 'center',
                                                fontWeight: 'bold',
                                                fontSize: '1rem',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    bgcolor: 'primary.light',
                                                    color: 'primary.contrastText',
                                                    borderColor: 'primary.dark'
                                                }
                                            }}
                                            onClick={() => {
                                                navigate('/decarbonization-actions');
                                            }}
                                        >
                                            Explore Decarbonization Actions
                                        </Box>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            endIcon={<ArrowForwardIcon />}
                                            sx={{
                                                flex: 1,
                                                width: '100%',
                                                fontSize: '1.1rem',
                                                fontWeight: 'bold',
                                                bgcolor: 'secondary.dark',
                                                '&:hover': {
                                                    bgcolor: 'secondary.main'
                                                }
                                            }}
                                            onClick={() => {
                                                navigate(`/buildings/${id}/optimization`);
                                            }}
                                        >
                                            Optimization Actions
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Card>

            <BuildingDetailsInsights id={id} />

            <Box sx={{ my: 4, display: 'flex', justifyContent: 'center' }}>
                <Button
                    sx={{ mx: 1 }}
                    variant={view === 'floors' ? 'contained' : 'outlined'}
                    onClick={() => setView('floors')}
                >
                    Floors
                </Button>
                <Button
                    sx={{ mx: 1 }}
                    variant={view === 'ceilings' ? 'contained' : 'outlined'}
                    onClick={() => setView('ceilings')}
                >
                    Ceilings
                </Button>
                <Button
                    sx={{ mx: 1 }}
                    variant={view === 'walls' ? 'contained' : 'outlined'}
                    onClick={() => setView('walls')}
                >
                    Walls
                </Button>
                <Button
                    sx={{ mx: 1 }}
                    variant={view === 'appliances' ? 'contained' : 'outlined'}
                    onClick={() => setView('appliances')}
                >
                    Appliances
                </Button>
                {/* <Button
                    sx={{ mx: 1 }}
                    variant={view === 'hot_water' ? 'contained' : 'outlined'}
                    onClick={() => setView('hot_water')}
                >
                    Hot Water Systems
                </Button> */}
                <Button
                    sx={{ mx: 1 }}
                    variant={view === 'heating_systems' ? 'contained' : 'outlined'}
                    onClick={() => setView('heating_systems')}
                >
                    Heating Systems
                </Button>
                <Button
                    sx={{ mx: 1 }}
                    variant={view === 'lamps' ? 'contained' : 'outlined'}
                    onClick={() => setView('lamps')}
                >
                    Lighting
                </Button>
                <Button
                    sx={{ mx: 1 }}
                    variant={view === 'acs' ? 'contained' : 'outlined'}
                    onClick={() => setView('acs')}
                >
                    Air Conditions
                </Button>
            </Box>

            {/* Conditional Rendering */}
            {view === 'floors' ? <BuildingDetailsFloors />
                : view === 'walls' ? <BuildingDetailsWalls />
                    : view === 'appliances' ? <BuildingDetailsAppliances />
                        : view === 'hot_water' ? <BuildingDetailsHotWater />
                            : view === 'lamps' ? <BuildingDetailsLamps />
                                : view === 'acs' ? <BuildingDetailsACs />
                                    : view === 'ceilings' ? <BuildingDetailsCeilings />
                                        : <BuildingDetailsHeatingSystems />}

            {/* Edit Modal */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)}
                PaperProps={{ sx: { minWidth: 600, maxWidth: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }, style: { width: '100%' } }}
                fullWidth
            >
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100%' }}>
                    <EditBuilding
                        building={building}
                        onClose={() => setEditOpen(false)}
                        onSuccess={fetchBuilding}
                        onError={() => { }}
                    />
                </Box>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <Typography variant="h6" sx={{ p: 3, pb: 0 }}>
                    Are you sure you want to delete this building?
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, pt: 0, mt: 3 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="primary" variant="outlined" sx={{ mr: 2 }} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </Box>
            </Dialog>
        </Container>
    );
};

export default BuildingDetails;
